import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Shield, Camera, Save, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const Profile: React.FC = () => {
  const { user, token } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [fullName, setFullName] = useState(user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put('https://api.lifeshare.app/api/v1/users/me', { full_name: fullName, email: email }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage({ type: 'success', text: 'Profil başarıyla güncellendi!' });
      setIsEditing(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Profil güncellenemedi' });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setMessage({ type: 'error', text: 'Dosya boyutu 5MB\'dan küçük olmalıdır' }); return; }
    if (!file.type.startsWith('image/')) { setMessage({ type: 'error', text: 'Sadece resim dosyaları yüklenebilir' }); return; }

    setUploadingAvatar(true);
    setMessage(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post('https://api.lifeshare.app/api/v1/users/me/avatar', formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      setMessage({ type: 'success', text: 'Profil resmi başarıyla güncellendi!' });
      setTimeout(() => window.location.reload(), 1500);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Resim yüklenemedi' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { setMessage({ type: 'error', text: 'Yeni şifreler eşleşmiyor' }); return; }
    if (newPassword.length < 6) { setMessage({ type: 'error', text: 'Yeni şifre en az 6 karakter olmalıdır' }); return; }

    setSaving(true);
    setMessage(null);
    try {
      await axios.put('https://api.lifeshare.app/api/v1/users/password', { current_password: oldPassword, new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage({ type: 'success', text: 'Şifre başarıyla değiştirildi!' });
      setShowPasswordForm(false);
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Şifre değiştirilemedi' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Profil Ayarları</h1>
            <p className="text-sm text-gray-500 mt-1">Profil ve şifre yönetimi</p>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-2xl flex items-start gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />}
            <div>
              <p className={`font-medium ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{message.type === 'success' ? 'Başarılı' : 'Hata'}</p>
              <p className={`text-sm ${message.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Avatar & Quick Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 text-center">
              <div className="relative inline-block">
                {user?.avatar_url ? (
                  <img src={`https://api.lifeshare.app${user.avatar_url}`} alt="Avatar" className="w-28 h-28 rounded-full mx-auto shadow-lg object-cover border-4 border-purple-100" />
                ) : (
                  <div className="w-28 h-28 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto shadow-lg">
                    {user?.username?.charAt(0).toUpperCase()}
                  </div>
                )}
                <button onClick={handleAvatarClick} disabled={uploadingAvatar} className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg border-2 border-purple-500 hover:bg-purple-50 transition">
                  {uploadingAvatar ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" /> : <Camera className="w-5 h-5 text-purple-600" />}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
              </div>

              <h2 className="text-xl font-bold text-gray-900 mt-4">{user?.full_name || user?.username}</h2>
              <p className="text-sm text-gray-500 mt-1">@{user?.username}</p>

              <div className="mt-4 inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                <Shield className="w-4 h-4 mr-1.5" />
                {user?.admin_role?.replace('_', ' ').toUpperCase() || 'ADMIN'}
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700 truncate">{user?.email}</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">ID: {user?.id}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Info & Password */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Information */}
            <div className="bg-white rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Profil Bilgileri</h3>
                {!isEditing && (
                  <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition text-sm font-medium">Düzenle</button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ad Soyad</label>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={!isEditing} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500 disabled:text-gray-500" placeholder="Ad Soyad" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-posta</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!isEditing} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500 disabled:text-gray-500" placeholder="email@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kullanıcı Adı</label>
                  <input type="text" value={user?.username} disabled className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-500 cursor-not-allowed" />
                  <p className="text-xs text-gray-400 mt-1">Kullanıcı adı değiştirilemez</p>
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-3 mt-6">
                  <button onClick={handleSaveProfile} disabled={saving} className="px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition flex items-center gap-2 disabled:opacity-50">
                    {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Save className="w-4 h-4" />}Kaydet
                  </button>
                  <button onClick={() => { setIsEditing(false); setFullName(user?.full_name || ''); setEmail(user?.email || ''); }} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition">İptal</button>
                </div>
              )}
            </div>

            {/* Password Change */}
            <div className="bg-white rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-orange-100 rounded-xl"><Lock className="w-5 h-5 text-orange-600" /></div>
                  <h3 className="text-lg font-semibold text-gray-900">Şifre Değiştir</h3>
                </div>
                {!showPasswordForm && (
                  <button onClick={() => setShowPasswordForm(true)} className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition text-sm font-medium">Şifre Değiştir</button>
                )}
              </div>

              {showPasswordForm ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mevcut Şifre</label>
                    <div className="relative">
                      <input type={showOldPassword ? 'text' : 'password'} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500 pr-10" placeholder="Mevcut şifreniz" />
                      <button type="button" onClick={() => setShowOldPassword(!showOldPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showOldPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Yeni Şifre</label>
                    <div className="relative">
                      <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500 pr-10" placeholder="Yeni şifreniz (en az 6 karakter)" />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Yeni Şifre (Tekrar)</label>
                    <div className="relative">
                      <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500 pr-10" placeholder="Yeni şifrenizi tekrar girin" />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button onClick={handleChangePassword} disabled={saving || !oldPassword || !newPassword || !confirmPassword} className="px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition flex items-center gap-2 disabled:opacity-50">
                      {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Lock className="w-4 h-4" />}Şifreyi Değiştir
                    </button>
                    <button onClick={() => { setShowPasswordForm(false); setOldPassword(''); setNewPassword(''); setConfirmPassword(''); }} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition">İptal</button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Güvenliğiniz için şifrenizi düzenli olarak değiştirmenizi öneririz.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
